// src/pages/MsFelderEditor.jsx
import { C, Field, TextInput, NumberInput, Select, Row, ComboInput } from './formui';
import { typenFuerMsHersteller } from '../lib/typenlisten';
import { MS_FELD_TYPEN } from '../lib/auswahllisten';
import SchutzgeraetBlock, { SchutzpruefDateien, hatSchutzgeraet } from './SchutzgeraetBlock';

// HerstellerPicker: robuste Mobile-freundliche Variante statt HTML5-datalist.
// Zeigt die haeufigen Hersteller als anklickbare Chips ueber dem Eingabefeld.
// Freitext bleibt jederzeit moeglich. Aktiver Chip wird hervorgehoben.
export function HerstellerPicker({ value, onChange, options }) {
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
        {options.map(opt => {
          const active = (value || '').trim().toLowerCase() === opt.toLowerCase();
          return (
            <button key={opt} type='button' onClick={() => onChange(opt)} style={{
              padding: '6px 10px', borderRadius: 14, fontSize: 14, cursor: 'pointer',
              background: active ? C.navy : C.white,
              color: active ? C.yellow : C.navy,
              border: '1.5px solid ' + (active ? C.navy : C.gray200),
              fontWeight: active ? 700 : 600,
            }}>{opt}</button>
          );
        })}
      </div>
      <TextInput value={value} onChange={onChange} placeholder='oder eigenen Hersteller eintippen' />
    </div>
  );
}

// Vorschlagsliste der gaengigen MS-Anlagen-Hersteller (15.05.: erweiterbar).
// Wird als Chips angezeigt - Freitext bleibt moeglich.
export const MS_HERSTELLER_VORSCHLAEGE = ['Siemens', 'ABB', 'Ormazabal', 'Schneider Electric', 'EATON', 'Driescher'];

// stationId + darfHochladen: Schutzpruefprotokolle je Leistungsschalterfeld (Buero, Migration 58).
export default function MsFelderEditor({ items, setItems, defaultHersteller, stationId, darfHochladen = false }) {
  // Beim Hinzufuegen: Werte vom letzten Feld uebernehmen (Hersteller, Typ/Modell,
  // Herstellungsjahr), damit Monteure nicht jedes Feld einzeln tippen muessen.
  const add = () => {
    const last = items[items.length - 1];
    const inherit = last ? {
      hersteller: last.hersteller || defaultHersteller || '',
      typ: last.typ || '',
      herstellungsjahr: last.herstellungsjahr ?? null,
    } : {
      hersteller: defaultHersteller || '',
      typ: '',
      herstellungsjahr: null,
    };
    setItems([...items, {
      typ_code: 'R', bezeichnung: '', feld_nr: '',
      hersteller: inherit.hersteller,
      typ: inherit.typ,
      seriennummer: '',
      herstellungsjahr: inherit.herstellungsjahr,
      notizen: '',
    }]);
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const update = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  return (
    <>
      {defaultHersteller && (
        <div style={{ padding: 10, marginBottom: 10, background: C.yellowBg, borderRadius: 7, fontSize: 14, color: C.navy }}>
          Hersteller wird automatisch mit "{defaultHersteller}" vorbelegt. Beim Hinzufuegen weiterer Felder werden auch Typ/Modell und Herstellungsjahr vom vorherigen Feld uebernommen - pro Feld aenderbar.
        </div>
      )}
      {items.length === 0 ? (
        <div style={{ padding: 14, color: C.gray500, fontSize: 15, fontStyle: 'italic', textAlign: 'center' }}>Noch keine MS-Felder.</div>
      ) : items.map((f, i) => (
        <div key={i} style={{ padding: 12, marginBottom: 10, background: C.gray100, borderRadius: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontWeight: 700, color: C.navy, fontSize: 15 }}>Feld {i + 1}</div>
            <button onClick={() => remove(i)} style={{ background: C.redBg, color: C.red, border: 'none', borderRadius: 8, padding: '8px 14px', minHeight: 44, cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>Entfernen</button>
          </div>
          <Row columns={2}>
            <Field label='Typ' required><Select value={f.typ_code} onChange={v => update(i, 'typ_code', v)} options={MS_FELD_TYPEN}/></Field>
            <Field label='Bezeichnung / Feld-Nr' hint='z.B. "J01"'><TextInput value={f.bezeichnung} onChange={v => update(i, 'bezeichnung', v)}/></Field>
          </Row>
          <Row columns={2}>
            <Field label='Hersteller' hint='Auf Vorschlag tippen oder eigenen Hersteller eingeben'>
              <HerstellerPicker value={f.hersteller} onChange={v => update(i, 'hersteller', v)} options={MS_HERSTELLER_VORSCHLAEGE}/>
            </Field>
            {/* Baureihen des gewaehlten Herstellers (z. B. Siemens -> 8DJH ...). */}
            <Field label='Typ / Modell'><ComboInput value={f.typ} onChange={v => update(i, 'typ', v)} options={typenFuerMsHersteller(f.hersteller || defaultHersteller)} placeholder='z. B. 8DJH'/></Field>
          </Row>
          <Row columns={2}>
            <Field label='Seriennummer'><TextInput value={f.seriennummer} onChange={v => update(i, 'seriennummer', v)}/></Field>
            <Field label='Herstellungsjahr'><NumberInput value={f.herstellungsjahr} onChange={v => update(i, 'herstellungsjahr', v)}/></Field>
          </Row>
          {/* Leistungsschalterfeld: UMZ-/Schutzgeraet und Pruefprotokolle (Anna-Lena 18.09.2026) */}
          {hatSchutzgeraet(f) && (
            <>
              <SchutzgeraetBlock wert={f.schutzgeraet} onChange={v => update(i, 'schutzgeraet', v)}/>
              {stationId && <SchutzpruefDateien stationId={stationId} feldId={f.id} darfHochladen={darfHochladen}/>}
            </>
          )}
        </div>
      ))}
      <button onClick={add} style={{ marginTop: 4, padding: '10px 16px', background: C.navy, color: C.yellow, border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>+ MS-Feld hinzufuegen</button>
    </>
  );
}
